/*
 * Kartenbild in der Warenkorb-Ortskarte, samt Vollbild-Vorschau.
 *
 * Die Bild-URL steht bereits als Line Item Property `_Vorschau` im Warenkorb;
 * der Konfigurator schreibt sie beim Speichern des Ausschnitts. Dieses Element
 * setzt sie ein und leitet sie an den Dialog weiter – es kennt weder
 * Koordinaten noch Zoom und berechnet keinen davon.
 *
 * Eingesetzt wird per JavaScript und nicht im Liquid, weil das Bild von einem
 * Dritten kommt. Liegt kein AVV vor (`consent_mode: click`), ist der Klick auf
 * „Karte laden“ auf der Produktseite die Einwilligung in die Übertragung der
 * IP-Adresse. Ein serverseitig gerendertes <img> wuerde sie umgehen.
 */
if (!customElements.get('cart-map-preview')) {
  customElements.define(
    'cart-map-preview',
    class CartMapPreview extends HTMLElement {
      static CONSENT_STORAGE_KEY = 'cmc-geoapify-consent';

      /* Kantenlaenge der Fassung fuer den Dialog. Der Zoom muss mitwachsen. */
      static LARGE_PX = 800;

      connectedCallback() {
        /* Die Section Rendering API haengt dasselbe Element erneut ein. */
        if (this.firstElementChild) return;

        /*
         * Ohne URL gibt es nichts zu zeigen – dann sieht die Ortszeile aus wie
         * ohne diese Erweiterung. Ohne Einwilligung dagegen SCHON: dort steht
         * ein Angebot statt einer leeren Flaeche.
         *
         * Die Einwilligung liegt im sessionStorage und stirbt mit dem Tab. Wer
         * spaeter ueber den Warenkorb-Link zurueckkommt, sah bis hierhin nur
         * die Adresse als Text – und konnte den einen Wert nicht mehr pruefen,
         * bei dem ein Fehler ein unverkaeufliches Einzelstueck erzeugt.
         */
        if (!this.dataset.src) return;
        if (!this.hasConsent()) {
          this.renderPlaceholder();
          return;
        }

        this.renderImage();
      }

      /*
       * Der Platzhalter ist zugleich die Einwilligung: der Klick auf „Vorschau
       * laden“ ist die Handlung, mit der der Kunde der Uebertragung seiner
       * IP-Adresse zustimmt. Deshalb steht der Hinweis darauf sichtbar daneben
       * und nicht als title-Attribut – eine Einwilligung, die man nicht liest,
       * ist keine.
       *
       * Damit holt der Warenkorb sie selbst ein, statt sich auf die vom
       * Konfigurator mitgereichte zu verlassen.
       */
      renderPlaceholder(text, label) {
        this.classList.add('cart-item__map-preview--pending');

        const box = document.createElement('div');
        box.className = 'cart-item__map-placeholder';

        const note = document.createElement('p');
        note.className = 'cart-item__map-placeholder-note';
        note.textContent =
          text ||
          'Die Kartenvorschau kommt von unserem Kartendienst Geoapify. Beim Laden wird deine IP-Adresse übertragen.';

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cart-item__map-load';
        button.textContent = label || 'Vorschau laden';
        button.addEventListener('click', () => {
          this.rememberConsent();
          this.classList.remove('cart-item__map-preview--pending');
          this.replaceChildren();
          this.renderImage();
        });

        box.append(note, button);
        this.appendChild(box);
      }

      /*
       * Derselbe Schluessel, den city-map-configurator.js auf der Produktseite
       * schreibt. Die Einwilligung gilt fuer den ganzen Besuch, egal wo sie
       * erteilt wurde.
       */
      rememberConsent() {
        try {
          sessionStorage.setItem(CartMapPreview.CONSENT_STORAGE_KEY, '1');
        } catch (error) {
          /* Privater Modus: das Bild laedt trotzdem, nur nicht beim naechsten Aufruf. */
        }
      }

      renderImage() {
        const image = document.createElement('img');
        image.src = this.dataset.src;
        /*
         * Das Bild ist die erste Angabe der Zeile und steht entsprechend gross
         * – 400 px reichen dafuer auf keinem Retina-Display. Die 800-px-Fassung
         * ist dieselbe, die der Dialog laedt; dort kommt sie dann aus dem Cache.
         */
        image.srcset = `${this.dataset.src} 400w, ${this.largeUrl()} 800w`;
        image.sizes = '(min-width: 750px) 24rem, 55vw';
        image.alt = this.dataset.alt || '';
        image.width = 400;
        image.height = 400;
        image.loading = 'lazy';
        image.decoding = 'async';

        /*
         * Antwortet der Kartendienst nicht, bleibt nicht nichts stehen: der
         * Kunde erfaehrt, dass die Vorschau fehlt, und kann es erneut
         * versuchen. Eine kaputte Flaeche waere die schlechteste der drei
         * Moeglichkeiten.
         */
        image.addEventListener('error', () => {
          this.replaceChildren();
          this.renderPlaceholder('Die Kartenvorschau ließ sich gerade nicht laden.', 'Erneut versuchen');
        });
        image.addEventListener('load', () => this.revealAttribution(), { once: true });

        /*
         * Ein Button und kein klickbares <div>: Tastatur, Screenreader und die
         * Fokusanzeige des Themes kommen damit von selbst. Nur wenn es einen
         * Dialog gibt – sonst bliebe ein Button ohne Wirkung stehen.
         */
        const dialog = document.querySelector('[data-cart-map-dialog]');
        if (!dialog || typeof dialog.showModal !== 'function') {
          this.appendChild(image);
          const nurMarker = this.markerOverlay();
          if (nurMarker) this.appendChild(nurMarker);
          return;
        }

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cart-item__map-button';
        button.appendChild(image);

        const marker = this.markerOverlay();
        if (marker) button.appendChild(marker);

        const hint = document.createElement('span');
        hint.className = 'cart-item__map-zoom';
        hint.setAttribute('aria-hidden', 'true');
        hint.innerHTML = CartMapPreview.ZOOM_ICON;
        button.appendChild(hint);

        const label = document.createElement('span');
        label.className = 'visually-hidden';
        label.textContent = `Ausschnitt${this.dataset.ort ? ` um ${this.dataset.ort}` : ''} vergrößern`;
        button.appendChild(label);

        button.addEventListener('click', () => this.openDialog(dialog));
        this.appendChild(button);
      }

      hasConsent() {
        if (this.dataset.consentMode === 'auto') return true;
        try {
          return sessionStorage.getItem(CartMapPreview.CONSENT_STORAGE_KEY) === '1';
        } catch (error) {
          /* Privater Modus – ohne nachweisbare Einwilligung bleibt das Bild aus. */
          return false;
        }
      }

      /*
       * Der OSM-Credit steht einmal ganz unten und erscheint erst, wenn
       * tatsaechlich eine Karte zu sehen ist. Die Klasse sitzt an <html> und
       * nicht am Absatz selbst: Warenkorb-Updates rendern die Liste und den
       * Footer einzeln neu, und ein frisch eingesetzter Footer wuerde ein
       * gesetztes Attribut wieder mitbringen – die Klasse ueberlebt beides.
       */
      revealAttribution() {
        document.documentElement.classList.add('cart-map-preview-loaded');
      }

      /*
       * Dieselbe Mitte, derselbe Ausschnitt, nur groesser: doppelte Kante
       * heisst Zoom plus eins. Wird hier gerechnet und nicht in der Property,
       * weil die auf 250 Zeichen begrenzt ist und beim Hinzufuegen einfriert.
       */
      largeUrl() {
        const size = CartMapPreview.LARGE_PX;
        const faktor = size / 400;
        return this.dataset.src
          .replace(/([?&])width=\d+&height=\d+/, `$1width=${size}&height=${size}`)
          .replace(/([?&]zoom=)([\d.]+)/, (all, key, zoom) =>
            key + (parseFloat(zoom) + Math.log2(faktor)).toFixed(2)
          );
      }

      /*
       * Die gesetzte Markierung als Overlay. Sie steckt nicht im Kartenbild:
       * Der Kartendienst kennt nur seine eigenen Symbole in eigener Form, der
       * Kunde saehe etwas anderes als das, was er gewaehlt hat. Gezeichnet
       * wird deshalb aus demselben Sprite wie auf der Produktseite.
       *
       * Das Bild zeigt genau den bestellten Ausschnitt, deshalb ist die
       * Position in Millimetern auf dem Druckformat direkt ein Prozentwert
       * darin. Ankerpunkte und Farben stehen in component-map-marker.css.
       */
      markerOverlay() {
        const { markerMm, markerSymbol, markerColor, printMm } = this.dataset;
        if (!markerMm || !markerSymbol) return null;

        const [mmX, mmY] = markerMm.split(',').map(Number);
        const kante = parseFloat(printMm);
        if (!Number.isFinite(mmX) || !Number.isFinite(mmY) || !kante) return null;

        const span = document.createElement('span');
        span.className = 'map-marker';
        span.dataset.symbol = markerSymbol;
        span.dataset.color = markerColor || 'rot';
        span.style.setProperty('--map-marker-x', `${((mmX / kante) * 100).toFixed(2)}%`);
        span.style.setProperty('--map-marker-y', `${((mmY / kante) * 100).toFixed(2)}%`);
        span.innerHTML =
          '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
          `<use href="#CartMap-sym-${markerSymbol}"></use></svg>`;
        return span;
      }

      openDialog(dialog) {
        const image = dialog.querySelector('[data-cart-map-dialog-image]');
        const place = dialog.querySelector('[data-cart-map-dialog-place]');
        const meta = dialog.querySelector('[data-cart-map-dialog-meta]');
        const addresses = dialog.querySelector('[data-cart-map-dialog-addresses]');

        place.textContent = this.dataset.ort || '';

        const teile = [];
        if (this.dataset.box) teile.push(`${this.dataset.box} × ${this.dataset.box} m Ausschnitt`);
        if (this.dataset.markierung) teile.push(this.dataset.markierung);
        meta.textContent = teile.join(' · ');
        meta.hidden = teile.length === 0;

        if (addresses) {
          const zeilen = [];
          if (this.dataset.mitte) zeilen.push(`Kartenmitte: ${this.dataset.mitte}`);
          if (this.dataset.markerAdresse) zeilen.push(`Markierung bei: ${this.dataset.markerAdresse}`);
          addresses.replaceChildren(
            ...zeilen.map((text) => {
              const zeile = document.createElement('span');
              zeile.textContent = text;
              return zeile;
            })
          );
          addresses.hidden = zeilen.length === 0;
        }

        /*
         * Erst das Thumbnail aus dem Cache, damit sofort etwas zu sehen ist –
         * die grosse Fassung braucht rund drei Sekunden. Sie ersetzt es nur,
         * wenn sie ankommt; bleibt sie aus, steht die kleine weiter da.
         */
        image.alt = this.dataset.alt || '';
        const gezeigt = this.querySelector('img')?.currentSrc;
        image.src = gezeigt || this.dataset.src;
        image.classList.add('is-loading');

        const figur = dialog.querySelector('[data-cart-map-dialog-figure]');
        figur.querySelector('.map-marker')?.remove();
        const marker = this.markerOverlay();
        if (marker) figur.appendChild(marker);

        const gross = new Image();
        gross.addEventListener('load', () => {
          image.src = gross.src;
          image.classList.remove('is-loading');
        });
        gross.addEventListener('error', () => image.classList.remove('is-loading'));
        gross.src = this.largeUrl();

        dialog.showModal();
      }

      static ZOOM_ICON =
        '<svg class="icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 19 19" focusable="false">' +
        '<path fill="currentColor" fill-rule="evenodd" d="M4.667 7.94a.5.5 0 0 1 .499-.501l5.534-.014a.5.5 0 1 1 .002 1l-5.534.014a.5.5 0 0 1-.5-.5" clip-rule="evenodd"/>' +
        '<path fill="currentColor" fill-rule="evenodd" d="M7.926 4.665a.5.5 0 0 1 .501.498l.014 5.534a.5.5 0 1 1-1 .003l-.014-5.534a.5.5 0 0 1 .499-.501" clip-rule="evenodd"/>' +
        '<path fill="currentColor" fill-rule="evenodd" d="M12.832 3.03a6.931 6.931 0 1 0-9.802 9.802 6.931 6.931 0 0 0 9.802-9.802M2.323 2.323a7.931 7.931 0 0 1 11.296 11.136l4.628 4.628a.5.5 0 0 1-.707.707l-4.662-4.662A7.932 7.932 0 0 1 2.323 2.323" clip-rule="evenodd"/></svg>';
    }
  );
}

/*
 * Der Dialog selbst: Schliessen, Klick auf den Backdrop und der Weg zur Kasse.
 * Einmal pro Seite verdrahtet, nicht pro Thumbnail.
 */
document.addEventListener('click', (event) => {
  const dialog = document.querySelector('[data-cart-map-dialog]');
  if (!dialog || !dialog.open) return;

  if (event.target.closest('[data-cart-map-dialog-close]')) {
    dialog.close();
    return;
  }

  /* Klick neben den Inhalt schliesst – <dialog> selbst fuellt die ganze Flaeche. */
  if (event.target === dialog) dialog.close();
});
